import secureApi from '../../utils/secureApi';
import TokenManager from '../../utils/tokenManager';
import { getPaymentPreview, PaymentPreviewRequest, PaymentPreviewResponse } from '../tournament/paymentService';

// New API response structure
export interface PaymentDue {
  _id: string;
  ageCategory: string;
  totalAmount: number;
  amountPaid: number;
  balanceAmount: number;
  balanceAmountNextMilestone?: number; // Upcoming due amount
  milestoneDueDates: {
    milestone: string;
    dueDate: string;
    totalDuePercentage: number;
  }[];
  milestoneCompleted: string;
  tournamentInfo: {
    _id: string;
    name: string;
    shortName: string;
    logo: string;
  };
  teamInfo: {
    _id: string;
    name: string;
    shortName: string;
    logo: string;
  };
  playerCount: number;
  playerIds: string[]; // Player IDs from the dues API response
}

export interface PaymentDuesResponse {
  statusCode: number;
  status: string;
  message: string;
  data: {
    paginatedResults: PaymentDue[];
    totalCount: number;
    totalPages?: number;
  };
}

// Legacy interface for backward compatibility
export interface DuePayment {
  _id: string;
  tournamentName: string;
  team: string;
  ageCategory: string;
  players: number;
  totalFee: number;
  amountPaid: number;
  paidPercentage: number;
  balanceDue: number;
  dueDate: string;
  status: 'Due' | 'Overdue' | 'Paid';
}

export interface DueDetail {
  _id: string;
  tournamentName: string;
  team: string;
  ageCategory: string;
  players: number;
  feePerPlayer: number;
  subtotal: number;
  gst: number;
  total: number;
  minimumPayment: number;
  amountPaid: number;
  pendingPayment: number;
  dueDate: string;
  status: 'Due' | 'Overdue' | 'Paid';
  upcomingPayments?: {
    installment: string;
    amount: number;
    dueDate: string;
    status: 'Due' | 'Overdue' | 'Paid';
  }[];
}

// Get list of all payment dues using new API endpoint
export const getDuesList = async (pageNum: number = 1): Promise<PaymentDuesResponse> => {
  try {
    console.log('🔍 Dues Service - Calling API...');
    console.log('🔍 Dues Service - Page number:', pageNum);
    
    // Debug token information
    const token = TokenManager.getToken();
    console.log('🔍 Dues Service - Token available:', !!token);
    console.log('🔍 Dues Service - Token starts with Bearer:', token?.startsWith('Bearer '));
    console.log('🔍 Dues Service - Token length:', token?.length);
    console.log('🔍 Dues Service - Token preview:', token ? `${token.substring(0, 20)}...` : 'No token');
    
    // Check if user is authenticated
    if (!token) {
      throw new Error('No authentication token found. Please log in again.');
    }
    
    const response = await secureApi.get<PaymentDuesResponse>(
      '/api/v1/tournament-registration/payment-dues',
      {
        params: { pageNum }
      }
    );
    
    console.log('🔍 Dues Service - Response:', response);
    return response;
  } catch (error: any) {
    console.error('Error fetching payment dues:', error);
    console.error('Error response:', error.response?.data);
    console.error('Error status:', error.response?.status);
    
    // Handle different types of errors
    if (error.response?.status === 404) {
      // 404 means no dues found - this is not an error, just empty data
      return {
        statusCode: 200,
        status: 'success',
        message: 'No dues found',
        data: {
          paginatedResults: [],
          totalCount: 0
        }
      };
    } else if (error.response?.status === 403) {
      throw new Error('Access denied (403). Please check your permissions or contact support.');
    } else if (error.response?.data) {
      throw new Error(error.response.data.message || 'Failed to fetch payment dues');
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('Failed to fetch payment dues');
    }
  }
};

// Legacy function for backward compatibility
export const getLegacyDuesList = async (filters?: {
  ageCategory?: string;
  status?: string;
  teams?: string;
}) => {
  try {
    const response = await secureApi.get('/api/dues', { params: filters });
    return response;
  } catch (error) {
    console.error('Error fetching dues list:', error);
    throw error;
  }
};

// Get specific due detail
export const getDueDetail = async (dueId: string) => {
  try {
    const response = await secureApi.get(`/api/dues/${dueId}`);
    return response;
  } catch (error) {
    console.error('Error fetching due detail:', error);
    throw error;
  }
};

// Create Razorpay order for payment
export const createPaymentOrder = async (dueId: string, amount: number) => {
  try {
    const response = await secureApi.post(`/api/dues/${dueId}/create-order`, { amount });
    return response;
  } catch (error) {
    console.error('Error creating payment order:', error);
    throw error;
  }
};

// Verify payment
export const verifyPayment = async (dueId: string, paymentData: any) => {
  try {
    const response = await secureApi.post(`/api/dues/${dueId}/verify-payment`, paymentData);
    return response;
  } catch (error) {
    console.error('Error verifying payment:', error);
    throw error;
  }
};

// Download invoice
export const downloadInvoice = async (dueId: string) => {
  try {
    const response = await secureApi.get(`/api/dues/${dueId}/invoice`, {
      responseType: 'blob'
    });
    return response;
  } catch (error) {
    console.error('Error downloading invoice:', error);
    throw error;
  }
};

// =========================
// New Payment Flow APIs
// =========================

// Make milestone payment
export const makeMilestonePayment = async (registrationId: string, amountPaid: number) => {
  try {
    console.log('🔍 Making milestone payment for registration:', registrationId);
    console.log('🔍 Amount to pay:', amountPaid);
    
    const response = await secureApi.post(`/api/v1/tournament-registration/make-milestone-payment/${registrationId}`, {
      amountPaid
    });
    
    return response;
  } catch (error: any) {
    console.error('Error making milestone payment:', error);
    console.error('Error response:', error.response?.data);
    console.error('Error status:', error.response?.status);
    
    if (error.response?.status === 403) {
      throw new Error('Access denied (403). Please check your permissions or contact support.');
    } else if (error.response?.data) {
      throw new Error(error.response.data.message || 'Failed to process milestone payment');
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('Failed to process milestone payment');
    }
  }
};

// Create tournament payment order
export const createTournamentOrder = async (orderData: {
  registration: string;
  milestonePaymentId: string;
  moduleType: string;
  amountPaid: number;
}) => {
  try {
    console.log('🔍 Creating tournament payment order:', orderData);
    
    const response = await secureApi.post('/api/v1/payments/create-order-tournament', orderData);
    
    return response;
  } catch (error: any) {
    console.error('Error creating tournament payment order:', error);
    console.error('Error response:', error.response?.data);
    console.error('Error status:', error.response?.status);
    
    if (error.response?.status === 403) {
      throw new Error('Access denied (403). Please check your permissions or contact support.');
    } else if (error.response?.data) {
      throw new Error(error.response.data.message || 'Failed to create payment order');
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('Failed to create payment order');
    }
  }
}; 

// =========================
// Payment Preview for Dues
// =========================

/**
 * Get payment preview for dues payment
 * @param tournamentId - The tournament ID
 * @param registrationId - The registration ID (due ID)
 * @param requestData - Payment preview request data
 * @returns Promise<PaymentPreviewResponse> - Payment preview data
 */
export const getDuesPaymentPreview = async (
  tournamentId: string,
  registrationId: string,
  requestData: PaymentPreviewRequest
): Promise<PaymentPreviewResponse> => {
  try {
    console.log('🔍 Dues Payment Preview - Calling API...');
    console.log('🔍 Dues Payment Preview - Tournament ID:', tournamentId);
    console.log('🔍 Dues Payment Preview - Registration ID:', registrationId);
    console.log('🔍 Dues Payment Preview - Request data:', requestData);
    
    // Use the existing payment preview API
    const response = await getPaymentPreview(tournamentId, registrationId, requestData);
    
    console.log('🔍 Dues Payment Preview - Response:', response);
    return response;
  } catch (error: any) {
    console.error('Error fetching dues payment preview:', error);
    console.error('Error response:', error.response?.data);
    console.error('Error status:', error.response?.status);
    
    // Handle different types of errors
    if (error.response?.status === 403) {
      throw new Error('Access denied (403). Please check your permissions or contact support.');
    } else if (error.response?.data) {
      throw new Error(error.response.data.message || 'Failed to fetch payment preview');
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('Failed to fetch payment preview');
    }
  }
}; 

 